import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Activity, 
  User, 
  Building2, 
  DollarSign, 
  AlertCircle,
  CheckCircle,
  UserPlus,
  TrendingUp,
  Settings,
  RefreshCw,
  Filter
} from 'lucide-react';
import { superadminApi } from '../../utils/api';

interface ActivityEvent {
  id: string;
  timestamp: string;
  type: 'user_created' | 'company_created' | 'payment_received' | 'well_added' | 'system_alert' | 'login' | 'settings_changed';
  companyId: string;
  tenantName: string;
  userId?: string;
  userName?: string;
  description: string;
  metadata?: any;
  severity: 'info' | 'success' | 'warning' | 'error';
}

export default function ActivityFeed() {
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'users' | 'payments' | 'system'>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadActivities();
    
    let interval: any;
    if (autoRefresh) {
      interval = setInterval(loadActivities, 10000); // Refresh every 10 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const loadActivities = async () => {
    try {
      const data = await superadminApi.get('/activities');
      
      if (data && Array.isArray(data)) {
        setActivities(data);
      } else {
        // Mock data for demo
        const mockActivities: ActivityEvent[] = [
          {
            id: '1',
            timestamp: new Date(Date.now() - 5 * 60000).toISOString(),
            type: 'user_created',
            companyId: 'acme-oil',
            tenantName: 'Acme Oil',
            userId: 'user-123',
            userName: 'John Smith',
            description: 'New user created: John Smith (john@acmeoil.com)',
            severity: 'success',
          },
          {
            id: '2',
            timestamp: new Date(Date.now() - 15 * 60000).toISOString(),
            type: 'payment_received',
            companyId: 'summit-petro',
            tenantName: 'Summit Petro',
            description: 'Payment received: $450.00 for 90 wells',
            metadata: { amount: 450, wells: 90 },
            severity: 'success',
          },
          {
            id: '3',
            timestamp: new Date(Date.now() - 30 * 60000).toISOString(),
            type: 'company_created',
            companyId: 'eagle-energy',
            tenantName: 'Eagle Energy',
            description: 'New company onboarded: Eagle Energy',
            severity: 'info',
          },
          {
            id: '4',
            timestamp: new Date(Date.now() - 45 * 60000).toISOString(),
            type: 'well_added',
            companyId: 'acme-oil',
            tenantName: 'Acme Oil',
            description: '5 new wells added',
            metadata: { wellCount: 5 },
            severity: 'info',
          },
          {
            id: '5',
            timestamp: new Date(Date.now() - 60 * 60000).toISOString(),
            type: 'system_alert',
            companyId: 'system',
            tenantName: 'System',
            description: 'Storage usage exceeded 80% threshold',
            severity: 'warning',
          },
          {
            id: '6',
            timestamp: new Date(Date.now() - 90 * 60000).toISOString(),
            type: 'login',
            companyId: 'summit-petro',
            tenantName: 'Summit Petro',
            userId: 'admin-456',
            userName: 'Sarah Johnson',
            description: 'Admin login: Sarah Johnson',
            severity: 'info',
          },
          {
            id: '7',
            timestamp: new Date(Date.now() - 120 * 60000).toISOString(),
            type: 'settings_changed',
            companyId: 'acme-oil',
            tenantName: 'Acme Oil',
            userId: 'admin-123',
            userName: 'Admin User',
            description: 'Company settings updated: branding colors changed',
            severity: 'info',
          },
          {
            id: '8',
            timestamp: new Date(Date.now() - 180 * 60000).toISOString(),
            type: 'payment_received',
            companyId: 'acme-oil',
            tenantName: 'Acme Oil',
            description: 'Payment received: $625.00 for 125 wells',
            metadata: { amount: 625, wells: 125 },
            severity: 'success',
          },
        ];
        
        setActivities(mockActivities);
      }
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user_created': return <UserPlus className="h-5 w-5" />;
      case 'company_created': return <Building2 className="h-5 w-5" />;
      case 'payment_received': return <DollarSign className="h-5 w-5" />;
      case 'well_added': return <TrendingUp className="h-5 w-5" />;
      case 'system_alert': return <AlertCircle className="h-5 w-5" />;
      case 'login': return <User className="h-5 w-5" />;
      case 'settings_changed': return <Settings className="h-5 w-5" />;
      default: return <Activity className="h-5 w-5" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'success': return 'text-green-600 bg-green-50 border-green-200';
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'error': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const now = Date.now();
    const time = new Date(timestamp).getTime();
    const diff = now - time;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const filteredActivities = activities.filter(activity => {
    if (filter === 'all') return true;
    if (filter === 'users') return ['user_created', 'login'].includes(activity.type);
    if (filter === 'payments') return activity.type === 'payment_received';
    if (filter === 'system') return ['system_alert', 'settings_changed'].includes(activity.type);
    return true;
  });

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            Live Activity Feed
            {autoRefresh && (
              <Badge variant="outline" className="ml-2 text-green-600 border-green-600">
                <div className="w-2 h-2 bg-green-600 rounded-full mr-2 animate-pulse" />
                Live
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? 'bg-green-50 border-green-200' : ''}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Live' : 'Paused'}
            </Button>
          </div>
        </div>
        
        {/* Filter Tabs */}
        <div className="flex gap-2 mt-4">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button
            variant={filter === 'users' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('users')}
          >
            <User className="h-4 w-4 mr-1" />
            Users
          </Button>
          <Button
            variant={filter === 'payments' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('payments')}
          >
            <DollarSign className="h-4 w-4 mr-1" />
            Payments
          </Button>
          <Button
            variant={filter === 'system' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('system')}
          >
            <Settings className="h-4 w-4 mr-1" />
            System
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading activity...</div>
        ) : filteredActivities.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No activity to display</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredActivities.map((activity) => (
              <div
                key={activity.id}
                className={`flex gap-4 p-4 rounded-lg border ${getSeverityColor(activity.severity)}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="font-medium text-sm">
                        {activity.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          {activity.tenantName}
                        </Badge>
                        {activity.userName && (
                          <span className="text-xs text-gray-600">
                            by {activity.userName}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {getTimeAgo(activity.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}